import { useState, useEffect } from "react";
import { X, Loader2, PartyPopper, Flame, CheckCircle2, Mail, Users, Lightbulb, ShieldAlert, MessageSquareHeart, Brain, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { PRICING } from "@/lib/pricing";
import { Events } from "@/lib/analytics";
import { trackExperimentEvent } from "@/lib/experiments";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pre-launch "Reserve the $5.99/year deal" modal.
 * Captures the user's email into the MailerLite waitlist — NO payment taken.
 * At launch, these users get a promo code for the $5.99/yr Play Store product.
 */
export default function ReserveDealModal({ isOpen, onClose, source = "unknown", defaultEmail = "" }) {
  const [email, setEmail] = useState(defaultEmail);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    axios
      .get(`${BACKEND_URL}/api/early-access/count`)
      .then((res) => setWaitlistCount(res.data?.count ?? null))
      .catch(() => setWaitlistCount(null));
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleaned)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/early-access`, {
        email: cleaned,
        first_name: null,
      });
      if (res.data.success) {
        Events.upgradeClick("reserve_yearly_waitlist", source);
        trackExperimentEvent("teaser_density_v1", "reserve_submit");
        setSubmitted(true);
        try {
          if (window.ttq) window.ttq.track("CompleteRegistration", { content_name: "Pre-Launch Deal Reserve" });
        } catch (_) { /* noop */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="reserve-deal-modal"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="reserve-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/5 hover:bg-black/10 flex items-center justify-center text-[#6B7280]"
        >
          <X size={16} />
        </button>

        {!submitted ? (
          <>
            <div
              className="px-6 pt-7 pb-5 text-white relative"
              style={{
                background: "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 50%, #4FB3A5 100%)",
              }}
            >
              <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/20 text-white text-[11px] font-bold px-3 py-1 rounded-full mb-3">
                <Flame size={12} />
                LIMITED PRE-LAUNCH OFFER
              </div>
              <h2 className="text-2xl font-bold leading-tight mb-1">
                Reserve your $5.99/year deal
              </h2>
              <p className="text-white/85 text-sm leading-relaxed">
                Lock in the lowest price before SayItBetter launches on Google Play and the App Store.
              </p>
              {waitlistCount !== null && waitlistCount > 0 && (
                <div
                  className="inline-flex items-center gap-1.5 mt-3 bg-white/15 backdrop-blur-sm border border-white/20 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"
                  data-testid="reserve-waitlist-count"
                >
                  <Users size={11} />
                  {waitlistCount.toLocaleString()}+ others already on the waitlist
                </div>
              )}
            </div>

            <div className="px-6 py-5">
              {/* Reassurance block */}
              <div className="bg-[#EAF6F4] border border-[#4FB3A5]/30 rounded-xl p-3 mb-4 flex items-start gap-2">
                <CheckCircle2 size={16} className="text-[#4FB3A5] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[#1F3A5F] leading-relaxed">
                  <strong>No charge today.</strong> We'll email you a promo code when the app is live on Google Play or the App Store — then you activate for {PRICING.yearly.price}.
                </div>
              </div>

              {/* What's unlocking for pre-launchers */}
              <div className="mb-4" data-testid="reserve-features-teaser">
                <div className="flex items-center gap-1.5 mb-2">
                  <Sparkles size={12} className="text-[#4FB3A5]" />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#4FB3A5]">
                    What you're locking in
                  </span>
                </div>
                <ul className="grid grid-cols-1 gap-1.5 text-[12px]">
                  {[
                    { icon: Lightbulb,          text: "Unlimited rewrites + daily texting tips" },
                    { icon: ShieldAlert,        text: "Red-flag detector (gaslighting, breadcrumbing)" },
                    { icon: MessageSquareHeart, text: "Dating translator — decode mixed signals" },
                    { icon: Brain,              text: "AI that learns your style over time" },
                    { icon: PartyPopper,        text: "Plus every feature we ship, free, forever" },
                  ].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#1F3A5F]">
                      <f.icon size={13} className="text-[#4FB3A5] flex-shrink-0" />
                      <span className="leading-snug">{f.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A7B4]" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="pl-10 h-12 rounded-xl bg-white border-[#E5E7EB]"
                    data-testid="reserve-email-input"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-5 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-bold"
                  data-testid="reserve-submit-btn"
                >
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : "Reserve my $5.99 spot"}
                </Button>
              </form>

              <p className="text-center text-[11px] text-[#A0A7B4] mt-3 leading-relaxed">
                Spots are limited to early adopters. At launch, standard price returns to {PRICING.monthly.label}.
              </p>
            </div>
          </>
        ) : (
          <div className="px-6 py-8 text-center" data-testid="reserve-success">
            <div className="w-16 h-16 rounded-2xl bg-[#EAF6F4] flex items-center justify-center mx-auto mb-4">
              <PartyPopper size={28} className="text-[#4FB3A5]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1F3A5F] mb-2">You're on the list!</h2>
            <p className="text-[#6B7280] text-sm leading-relaxed mb-5">
              We'll email <span className="font-semibold">{email}</span> the moment SayItBetter goes live
              on Google Play or the App Store — along with your private promo code for the $5.99/year deal.
            </p>
            <Button
              onClick={onClose}
              className="w-full py-5 rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] text-white font-bold"
              data-testid="reserve-close-btn"
            >
              Got it — take me back
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
